import { type FC, type FormEvent, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DialogHeader, ErrorMessage } from "@/shared/components";
import {
  useInviteOrganizationUserMutation,
  type OrganizationRoleType,
} from "@/partner/entities/organizationUsers";
import { useRoleOptions } from "../model";

const FORM_ID = "invite-organization-user-form";

interface InviteOrganizationUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

// Стиль диалога: как TagForm (DialogHeader с кнопкой сабмита в actions,
// DialogContent sx={{ p: 1 }} → карточка Paper sx={{ p: 2 }} с содержимым),
// как в UserDialog/TagForm — не fullScreen.
export const InviteOrganizationUserDialog: FC<
  InviteOrganizationUserDialogProps
> = ({ open, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const roleOptions = useRoleOptions();

  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<OrganizationRoleType[]>([]);

  const resetForm = () => {
    setEmail("");
    setRoles([]);
  };

  const [inviteOrganizationUser, { loading, error }] =
    useInviteOrganizationUserMutation({
      onCompleted: () => {
        enqueueSnackbar(t("team.invite.success"), { variant: "success" });
        resetForm();
        onCompleted?.();
      },
      // Список сотрудников должен увидеть нового приглашённого сразу же.
      refetchQueries: ["OrganizationUsersList"],
    });

  const toggleRole = (role: OrganizationRoleType) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit = Boolean(email) && roles.length > 0;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!canSubmit) return;

    void inviteOrganizationUser({ variables: { input: { email, roles } } });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={handleClose}
    >
      {loading && <LinearProgress />}
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <DialogHeader
          title={t("team.invite.title")}
          onClose={handleClose}
          loading={loading}
          actions={
            <Button
              variant="contained"
              type="submit"
              form={FORM_ID}
              disabled={loading || !canSubmit}
            >
              {t("team.invite.submit")}
            </Button>
          }
        />

        <DialogContent sx={{ p: 1 }}>
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              {error && <ErrorMessage error={error} />}

              <TextField
                label={t("team.invite.email")}
                type="email"
                fullWidth
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />

              <Typography variant="caption" color="text.secondary">
                {t("team.invite.roles")}
              </Typography>

              <FormGroup>
                {roleOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    sx={{ alignItems: "flex-start", mb: 1 }}
                    control={
                      <Checkbox
                        checked={roles.includes(option.value)}
                        onChange={() => toggleRole(option.value)}
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    }
                  />
                ))}
              </FormGroup>
            </Stack>
          </Paper>
        </DialogContent>
      </form>
    </Dialog>
  );
};
